import { Head, router, useForm } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import CustomerForm from "./_form"

interface Option {
  id: number
  name: string
}

interface CustomerData {
  company_name: string
  first_name: string
  last_name: string

  address: string
  address2: string
  city: string
  state: string
  zip: string
  office_phone: string
  cell_phone: string
  home_phone: string
  fax: string
  email: string
  website: string
  notes: string
  internal_notes: string
  client_type_id: number | null
  sales_rep_id: number | null
  referral_label: string | null
}

interface Duplicate {
  id: number
  name: string
}

interface Props {
  customer: CustomerData
  client_types: Option[]
  users: Option[]
  errors?: Record<string, string[]>
  duplicates?: Duplicate[]
}

interface ReferringAttorney {
  client_type_id: number | null
  company_name: string
  first_name: string
  last_name: string
  phone: string
  email: string
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Customers", href: "/customers" },
  { title: "New", href: "/customers/new" },
]

const emptyAttorney: ReferringAttorney = {
  client_type_id: null,
  company_name: "",
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
}

export default function New({
  customer,
  client_types,
  users,
  errors,
  duplicates,
}: Props) {
  const [overrideChecked, setOverrideChecked] = useState(false)
  const [referralString, setReferralString] = useState("")
  const [, setReferralLabel] = useState("")
  const [referringAttorney, setReferringAttorney] = useState<ReferringAttorney>(
    { ...emptyAttorney },
  )
  const { data, setData, processing } = useForm({
    company_name: customer.company_name,
    first_name: customer.first_name,
    last_name: customer.last_name,
    address: customer.address,
    address2: customer.address2,
    city: customer.city,
    state: customer.state,
    zip: customer.zip,
    office_phone: customer.office_phone,
    cell_phone: customer.cell_phone,
    home_phone: customer.home_phone,
    fax: customer.fax,
    email: customer.email,
    website: customer.website,
    notes: customer.notes,
    internal_notes: customer.internal_notes,
    client_type_id: customer.client_type_id,
    sales_rep_id: customer.sales_rep_id,
  })

  const handleReferralChange = (refString: string, label: string) => {
    setReferralString(refString)
    setReferralLabel(label)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const params: Record<string, unknown> = {}
    if (overrideChecked) {
      params.override = "true"
    }
    if (referralString) {
      params.referral_string = referralString
    }
    // Include quick-add attorney data if filled
    const hasAttorney =
      referringAttorney.client_type_id &&
      (referringAttorney.company_name || referringAttorney.first_name)
    if (!referralString && hasAttorney) {
      params.referring_attorney = referringAttorney
    }
    router.post("/customers", { customer: data, ...params })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="New Customer" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">New Customer</h1>

        {duplicates && duplicates.length > 0 && (
          <div className="mb-4 rounded border border-red-300 bg-red-50 p-4">
            <p className="mb-2 font-semibold text-red-800">
              Similar Customers were found:
            </p>
            <ul className="mb-3 list-inside list-disc text-red-700">
              {duplicates.map((dup) => (
                <li key={dup.id}>
                  <a
                    href={`/customers/${dup.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-hpi-active-blue hover:underline"
                  >
                    {dup.name}
                  </a>
                </li>
              ))}
            </ul>
            <label className="flex items-center gap-2 text-sm text-red-800">
              <input
                type="checkbox"
                checked={overrideChecked}
                onChange={(e) => setOverrideChecked(e.target.checked)}
                className="rounded border-red-300"
              />
              Override &amp; Add Customer
            </label>
          </div>
        )}

        <CustomerForm
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Create Customer"
          client_types={client_types}
          users={users}
          referralString={referralString}
          onReferralChange={handleReferralChange}
          initialReferralLabel={customer.referral_label}
          showQuickAdd={true}
          referringAttorney={referringAttorney}
          onReferringAttorneyChange={setReferringAttorney}
        />
      </div>
    </AppLayout>
  )
}
